import { useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { hasValidAuthCookies, verifyAndRefreshToken } from '@/lib/authService';
import { logInfo, logError } from '@/utils/logUtils';

/**
 * Component that initializes and manages cookie-based authentication
 * This should be mounted early in the application lifecycle
 */
export function CookieAuthInitializer() {
  const { firebaseUser } = useAuth();

  // Initialize cookies on component mount and when user changes
  useEffect(() => {
    const initCookieAuth = async () => {
      try {
        // Check if we have valid auth cookies
        if (hasValidAuthCookies()) {
          logInfo('Valid auth cookies found');
          
          // If we have Firebase user, make sure cookies are in sync
          if (firebaseUser) {
            await verifyAndRefreshToken();
            logInfo('Auth cookies refreshed after initialization');
          } else {
            logInfo('Waiting for Firebase auth to sync with cookies...');
          }
        } else if (firebaseUser) {
          // We have a Firebase user but no cookies - set them
          await verifyAndRefreshToken();
          logInfo('Auth cookies created for existing Firebase user');
        } else {
          logInfo('No auth cookies or Firebase user found');
        }
      } catch (error) {
        logError('Error initializing cookie auth:', error);
      }
    };

    initCookieAuth();
    
    // Set up periodic cookie refresh
    const refreshInterval = setInterval(() => {
      if (firebaseUser) {
        verifyAndRefreshToken().catch(error => {
          logError('Cookie refresh error:', error);
        });
      }
    }, 30 * 60 * 1000); // Every 30 minutes
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [firebaseUser]);

  // This is a utility component with no UI
  return null;
} 